import { useState } from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  LayoutSubSidebar,
  LayoutSubHeader,
  LayoutPageTitle,
} from '@/features/layout';
import { EmptyState } from '@/shared/ui/EmptyState';
import {
  BulkActionBar,
  MergePatientsModal,
  NewPatientModal,
  PatientFilterRail,
  PatientTable,
  PatientSubHeader,
  usePatientListFilters,
} from '@/features/patient-list';
import { useListPatientsQuery } from '@/state/slices/patients';
import { usePageLoading } from '@/hooks/usePageLoading';
import styles from './Patients.module.css';

export default function Patients() {
  const navigate = useNavigate();
  const [isNewPatientOpen, setIsNewPatientOpen] = useState(false);
  const [selectedUuids, setSelectedUuids] = useState<string[]>([]);
  const [isMergeOpen, setIsMergeOpen] = useState(false);

  const {
    params,
    searchText,
    setSearchText,
    sortOption,
    setSortOption,
    selectedSites,
    onSitesChange,
    selectedStudyTypes,
    onStudyTypesChange,
    datePreset,
    onDatePresetChange,
    customDateRange,
    onCustomDateRangeChange,
    selectedReferringPhysicians,
    onReferringPhysiciansChange,
    selectedSonographers,
    onSonographersChange,
    hasActiveFilters,
    activeFilterCount,
    hasActiveSearch,
    clearFilters,
    clearSearch,
    onTableChange,
  } = usePatientListFilters();

  const { data, isFetching, isLoading } = useListPatientsQuery(params);

  usePageLoading(isLoading);

  const hasData = (data?.results?.length ?? 0) > 0;
  const hasSelection = selectedUuids.length > 0;

  const clearSelection = () => setSelectedUuids([]);

  const mergeUuids =
    selectedUuids.length === 2
      ? ([selectedUuids[0], selectedUuids[1]] as [string, string])
      : null;

  const renderEmptyState = () => {
    if (hasActiveSearch && hasActiveFilters) {
      return (
        <EmptyState
          headline="Your search and filters returned no results"
          subtext="Try adjusting your search or clearing your filters."
          action={{ label: 'Clear search', onClick: clearSearch }}
        >
          <Button type="link" onClick={clearFilters}>
            Reset filters
          </Button>
        </EmptyState>
      );
    }

    if (hasActiveSearch) {
      return (
        <EmptyState
          headline="No matching patients"
          subtext="No patients matched your search. Try a different term."
          action={{ label: 'Clear search', onClick: clearSearch }}
        />
      );
    }

    if (hasActiveFilters) {
      return (
        <EmptyState
          headline="No matching patients"
          subtext="Try clearing or adjusting your filters."
          action={{ label: 'Reset filters', onClick: clearFilters }}
        />
      );
    }

    return (
      <EmptyState
        headline="No patients yet"
        subtext="Patients appear here after studies are uploaded."
        action={{
          label: 'Go to Worklist',
          onClick: () => navigate('/worklist'),
        }}
      />
    );
  };

  return (
    <>
      <LayoutPageTitle title="Patients" />
      <LayoutSubSidebar title="Filters">
        <PatientFilterRail
          searchText={searchText}
          onSearchChange={setSearchText}
          selectedSites={selectedSites}
          onSitesChange={onSitesChange}
          selectedStudyTypes={selectedStudyTypes}
          onStudyTypesChange={onStudyTypesChange}
          datePreset={datePreset}
          onDatePresetChange={onDatePresetChange}
          customDateRange={customDateRange}
          onCustomDateRangeChange={onCustomDateRangeChange}
          selectedReferringPhysicians={selectedReferringPhysicians}
          onReferringPhysiciansChange={onReferringPhysiciansChange}
          selectedSonographers={selectedSonographers}
          onSonographersChange={onSonographersChange}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />
      </LayoutSubSidebar>

      <LayoutSubHeader>
        {hasSelection ? (
          <BulkActionBar
            totalCount={data?.count ?? 0}
            selectedCount={selectedUuids.length}
            onMerge={() => setIsMergeOpen(true)}
            onClearSelection={clearSelection}
          />
        ) : (
          <PatientSubHeader
            totalCount={data?.count ?? 0}
            sortOption={sortOption}
            onSortChange={setSortOption}
            hasActiveFilters={hasActiveFilters}
            activeFilterCount={activeFilterCount}
            onClearFilters={clearFilters}
            onNewPatient={() => setIsNewPatientOpen(true)}
          />
        )}
      </LayoutSubHeader>

      {hasData ? (
        <div className={styles.tableWrapper}>
          <PatientTable
            data={data}
            loading={isFetching}
            page={params.page}
            pageSize={params.size}
            onTableChange={onTableChange}
            selectedUuids={selectedUuids}
            onSelectionChange={setSelectedUuids}
            maxSelection={2}
          />
        </div>
      ) : (
        !isFetching && renderEmptyState()
      )}

      <NewPatientModal
        open={isNewPatientOpen}
        onClose={() => setIsNewPatientOpen(false)}
      />

      <MergePatientsModal
        open={isMergeOpen}
        uuids={mergeUuids}
        onClose={() => setIsMergeOpen(false)}
        onMerged={clearSelection}
      />
    </>
  );
}
